import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, TextField,
  InputAdornment, Button, Stack, CircularProgress, Alert, MenuItem, Select,
  FormControl, InputLabel, IconButton, Tooltip, Tabs, Tab, Dialog,
  DialogTitle, DialogContent, DialogActions, Switch, LinearProgress
} from '@mui/material';
import {
  People, CheckCircle, Warning, AttachMoney, Telegram, Search,
  Refresh, ArrowBack, CalendarToday, Phone, Email, Verified,
  Discount, Add, Delete, ToggleOn, ToggleOff, Close
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import PublicNavbar from '../components/common/PublicNavbar';
import PublicFooter from '../components/common/PublicFooter';
import { telegramSubscription } from '../services/api';
import useStore from '../store/useStore';

export default function AdminSubscribersPage() {
  const navigate = useNavigate();
  const token = useStore((s) => s.token);
  const user = useStore((s) => s.user);

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscribers: 0,
    expiredSubscribers: 0,
    todayPaymentsCount: 0,
    todayPaymentsAmount: 0,
    monthlyRevenue: 0,
  });
  const [subscribers, setSubscribers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [telegramFilter, setTelegramFilter] = useState('ALL');

  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'PERCENTAGE',
    discount_value: '',
    max_uses: '',
    valid_until: '',
    description: '',
  });
  const [submittingCoupon, setSubmittingCoupon] = useState(false);

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const res = await telegramSubscription.adminSubscribers();
      if (res.data?.success) {
        setStats(res.data.data.stats || {});
        setSubscribers(res.data.data.users || []);
      }
    } catch (err) {
      console.error('Failed to load subscribers:', err);
      toast.error(err.response?.data?.message || 'Failed to load subscriber records');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    setCouponsLoading(true);
    try {
      const res = await telegramSubscription.adminCoupons();
      if (res.data?.success) {
        setCoupons(res.data.coupons || []);
      }
    } catch (err) {
      console.error('Failed to load coupons:', err);
      toast.error(err.response?.data?.message || 'Failed to load coupons');
    } finally {
      setCouponsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
    fetchCoupons();
  }, [token]);

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!newCoupon.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }
    if (!newCoupon.discount_value || parseFloat(newCoupon.discount_value) <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    setSubmittingCoupon(true);
    try {
      const res = await telegramSubscription.createAdminCoupon(newCoupon);
      if (res.data?.success) {
        toast.success(res.data.message || 'Coupon created successfully!');
        setCreateDialogOpen(false);
        setNewCoupon({
          code: '',
          discount_type: 'PERCENTAGE',
          discount_value: '',
          max_uses: '',
          valid_until: '',
          description: '',
        });
        fetchCoupons();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create coupon');
    } finally {
      setSubmittingCoupon(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      const res = await telegramSubscription.toggleAdminCoupon(couponId);
      if (res.data?.success) {
        toast.success(res.data.message);
        setCoupons((prev) =>
          prev.map((c) => (c.id === couponId ? { ...c, is_active: res.data.isActive ? 1 : 0 } : c))
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle coupon status');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      const res = await telegramSubscription.deleteAdminCoupon(couponId);
      if (res.data?.success) {
        toast.success('Coupon deleted');
        setCoupons((prev) => prev.filter((c) => c.id !== couponId));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete coupon');
    }
  };

  const filteredSubscribers = subscribers.filter((sub) => {
    const q = searchQuery.toLowerCase();
    const matchesQuery =
      !q ||
      sub.name?.toLowerCase().includes(q) ||
      sub.email?.toLowerCase().includes(q) ||
      sub.mobile?.toLowerCase().includes(q) ||
      sub.telegramUsername?.toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === 'ALL' ||
      sub.subscriptionStatus === statusFilter;

    const matchesTelegram =
      telegramFilter === 'ALL' ||
      (telegramFilter === 'CONNECTED' && sub.telegramConnected) ||
      (telegramFilter === 'NOT_CONNECTED' && !sub.telegramConnected);

    return matchesQuery && matchesStatus && matchesTelegram;
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#060d17', color: '#f8fafc' }}>
      <PublicNavbar />

      <Box sx={{ py: 6 }}>
        <Container maxWidth="xl">
          {/* Top Bar with Navigation */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Telegram sx={{ color: '#0088cc', fontSize: 36 }} />
                Subscribers & Coupon Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                Monitor ₹1,000 monthly subscribers, bot linking, revenue metrics, and discount coupons
              </Typography>
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => {
                  fetchSubscribers();
                  fetchCoupons();
                }}
                sx={{
                  borderColor: 'rgba(255,255,255,0.15)',
                  color: '#93c5fd',
                  '&:hover': { borderColor: '#93c5fd', bgcolor: 'rgba(37,99,235,0.1)' },
                }}
              >
                Refresh Data
              </Button>
              <Button
                component={RouterLink}
                to="/dashboard"
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                }}
              >
                Superadmin Dashboard
              </Button>
            </Stack>
          </Box>

          {/* Tab Switcher */}
          <Box sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)', mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              sx={{
                '& .MuiTab-root': {
                  color: '#94a3b8',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  minHeight: 48,
                  '&.Mui-selected': { color: '#60a5fa' },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#2563eb', height: 3 },
              }}
            >
              <Tab icon={<People sx={{ fontSize: 20 }} />} iconPosition="start" label="Subscribers & Revenue" />
              <Tab icon={<Discount sx={{ fontSize: 20 }} />} iconPosition="start" label={`Coupons Management (${coupons.length})`} />
            </Tabs>
          </Box>

          {/* TAB 0: Subscribers & Analytics */}
          {activeTab === 0 && (
            <Box>
              {/* Stats Cards */}
              <Grid container spacing={3} sx={{ mb: 5 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                          TOTAL REGISTERED
                        </Typography>
                        <People sx={{ color: '#60a5fa', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#ffffff' }}>
                        {stats.totalUsers || subscribers.length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Platform signups
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#34d399', fontWeight: 600 }}>
                          ACTIVE SUBSCRIBERS
                        </Typography>
                        <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#10b981' }}>
                        {stats.activeSubscribers}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Valid ₹1,000 passes
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#fbbf24', fontWeight: 600 }}>
                          EXPIRED / DUE
                        </Typography>
                        <Warning sx={{ color: '#f59e0b', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#f59e0b' }}>
                        {stats.expiredSubscribers}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Needs renewal
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#93c5fd', fontWeight: 600 }}>
                          TODAY'S PAYMENTS
                        </Typography>
                        <AttachMoney sx={{ color: '#3b82f6', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#60a5fa' }}>
                        ₹{stats.todayPaymentsAmount?.toLocaleString('en-IN') || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {stats.todayPaymentsCount} transactions today
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#c4b5fd', fontWeight: 600 }}>
                          MONTHLY REVENUE
                        </Typography>
                        <AttachMoney sx={{ color: '#8b5cf6', fontSize: 20 }} />
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#a78bfa' }}>
                        ₹{stats.monthlyRevenue?.toLocaleString('en-IN') || 0}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Current calendar month
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Filters and Search */}
              <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, mb: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by subscriber name, email, mobile, or Telegram handle..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search sx={{ color: '#64748b' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} md={3.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#94a3b8' }}>Subscription Status</InputLabel>
                        <Select
                          value={statusFilter}
                          label="Subscription Status"
                          onChange={(e) => setStatusFilter(e.target.value)}
                          sx={{
                            color: '#ffffff',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                          }}
                        >
                          <MenuItem value="ALL">All Statuses</MenuItem>
                          <MenuItem value="ACTIVE">Active Subscriptions</MenuItem>
                          <MenuItem value="EXPIRED">Expired Subscriptions</MenuItem>
                          <MenuItem value="NO_SUBSCRIPTION">Never Subscribed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={6} md={3.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: '#94a3b8' }}>Telegram Linking</InputLabel>
                        <Select
                          value={telegramFilter}
                          label="Telegram Linking"
                          onChange={(e) => setTelegramFilter(e.target.value)}
                          sx={{
                            color: '#ffffff',
                            bgcolor: 'rgba(255,255,255,0.03)',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                          }}
                        >
                          <MenuItem value="ALL">All Users</MenuItem>
                          <MenuItem value="CONNECTED">Telegram Linked</MenuItem>
                          <MenuItem value="NOT_CONNECTED">Telegram Not Linked</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Subscribers Table */}
              <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>SUBSCRIBER</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>CONTACT</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>STATUS</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>VALIDITY PERIOD</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>TELEGRAM BOT</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>AMOUNT</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>REGISTERED</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                            <CircularProgress size={32} sx={{ color: '#2563eb' }} />
                          </TableCell>
                        </TableRow>
                      ) : filteredSubscribers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#64748b' }}>
                            No subscribers match your search criteria.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSubscribers.map((sub) => (
                          <TableRow key={sub.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 700 }}>
                                {sub.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                ID: #{sub.id} • @{sub.username}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                                {sub.email}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {sub.mobile}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={sub.subscriptionStatus}
                                size="small"
                                color={sub.subscriptionStatus === 'ACTIVE' ? 'success' : sub.subscriptionStatus === 'EXPIRED' ? 'warning' : 'default'}
                                sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                              />
                            </TableCell>

                            <TableCell sx={{ color: '#cbd5e1', fontSize: '0.82rem' }}>
                              {sub.endDate ? (
                                <Box>
                                  <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
                                    Ends: {new Date(sub.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </Typography>
                                  {sub.startDate && (
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      Started: {new Date(sub.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  No active window
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {sub.telegramConnected ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Telegram sx={{ color: '#0088cc', fontSize: 20 }} />
                                  <Box>
                                    <Typography variant="body2" sx={{ color: '#60a5fa', fontWeight: 600 }}>
                                      @{sub.telegramUsername || 'linked'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                      Chat ID: {sub.telegramChatId}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Chip
                                  label="Not Linked"
                                  size="small"
                                  sx={{ bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#f87171', fontSize: '0.75rem' }}
                                />
                              )}
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                ₹{sub.amount || 1000}
                              </Typography>
                              <Typography variant="caption" sx={{ color: sub.paymentStatus === 'COMPLETED' ? '#34d399' : '#94a3b8' }}>
                                {sub.paymentStatus}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                              {sub.registeredAt ? new Date(sub.registeredAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          )}

          {/* TAB 1: Coupons Management */}
          {activeTab === 1 && (
            <Box>
              {/* Header with Create Button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Discount sx={{ color: '#f59e0b' }} />
                    Active Discount Coupons
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Coupons created here can be applied by users on registration and renewal pages.
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    bgcolor: '#f59e0b',
                    color: '#000',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#d97706' },
                    px: 3,
                  }}
                >
                  Create New Coupon
                </Button>
              </Box>

              {/* Coupons Table */}
              <Card sx={{ bgcolor: '#0b192c', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>COUPON CODE</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>DISCOUNT</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>REDEMPTIONS</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>EXPIRY DATE</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>DESCRIPTION</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700 }}>STATUS</TableCell>
                        <TableCell sx={{ color: '#64748b', fontWeight: 700, textAlign: 'right' }}>ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {couponsLoading ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                            <CircularProgress size={32} sx={{ color: '#f59e0b' }} />
                          </TableCell>
                        </TableRow>
                      ) : coupons.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#64748b' }}>
                            No coupons created yet. Click "Create New Coupon" to add one.
                          </TableCell>
                        </TableRow>
                      ) : (
                        coupons.map((c) => (
                          <TableRow key={c.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={c.code}
                                  sx={{
                                    bgcolor: 'rgba(245,158,11,0.15)',
                                    color: '#f59e0b',
                                    fontWeight: 800,
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    border: '1px solid rgba(245,158,11,0.3)',
                                  }}
                                />
                              </Box>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: '#10b981' }}>
                                {c.discount_type === 'PERCENTAGE' ? `${c.discount_value}% OFF` : `₹${c.discount_value} FLAT`}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {c.discount_type === 'PERCENTAGE' ? `Saves ₹${Math.round(1000 * (c.discount_value / 100))}` : 'Direct discount'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                                {c.actual_redemptions || c.current_uses} / {c.max_uses ? c.max_uses : '∞ Unlimited'}
                              </Typography>
                              {c.max_uses > 0 && (
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(100, ((c.actual_redemptions || c.current_uses) / c.max_uses) * 100)}
                                  sx={{ height: 4, borderRadius: 2, mt: 0.5, bgcolor: 'rgba(255,255,255,0.1)' }}
                                />
                              )}
                            </TableCell>

                            <TableCell sx={{ color: '#cbd5e1' }}>
                              {c.valid_until
                                ? new Date(c.valid_until).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })
                                : 'No Expiry'}
                            </TableCell>

                            <TableCell sx={{ color: '#94a3b8', maxWidth: 200, fontSize: '0.85rem' }}>
                              {c.description || '—'}
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={c.is_active ? 'ACTIVE' : 'INACTIVE'}
                                size="small"
                                color={c.is_active ? 'success' : 'default'}
                                sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                              />
                            </TableCell>

                            <TableCell sx={{ textAlign: 'right' }}>
                              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                <Tooltip title={c.is_active ? 'Deactivate' : 'Activate'}>
                                  <Switch
                                    checked={Boolean(c.is_active)}
                                    onChange={() => handleToggleCoupon(c.id)}
                                    color="success"
                                    size="small"
                                  />
                                </Tooltip>
                                <Tooltip title="Delete Coupon">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteCoupon(c.id)}
                                    sx={{ color: '#64748b', '&:hover': { color: '#ef4444' } }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>
          )}
        </Container>
      </Box>

      {/* Create Coupon Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#0b192c', color: '#fff', borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Discount sx={{ color: '#f59e0b' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Create New Discount Coupon
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setCreateDialogOpen(false)} sx={{ color: '#94a3b8' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleCreateCoupon}>
          <DialogContent sx={{ py: 2 }}>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Coupon Code *"
                placeholder="e.g. DIWALI50, FLAT200, VIPPROMO"
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                helperText="Characters are automatically capitalized"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#94a3b8' }}>Discount Type *</InputLabel>
                    <Select
                      value={newCoupon.discount_type}
                      label="Discount Type *"
                      onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      <MenuItem value="PERCENTAGE">Percentage (%)</MenuItem>
                      <MenuItem value="FIXED_AMOUNT">Fixed Amount (₹)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={newCoupon.discount_type === 'PERCENTAGE' ? 'Percentage (1-100) *' : 'Discount in ₹ *'}
                    type="number"
                    placeholder={newCoupon.discount_type === 'PERCENTAGE' ? '20' : '200'}
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Redemptions"
                    type="number"
                    placeholder="Leave empty for unlimited"
                    value={newCoupon.max_uses}
                    onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Valid Until"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={newCoupon.valid_until}
                    onChange={(e) => setNewCoupon({ ...newCoupon, valid_until: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      },
                      '& .MuiInputLabel-root': { color: '#94a3b8' },
                    }}
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Description / Campaign Name"
                placeholder="e.g. Special promotional discount for festival launch"
                value={newCoupon.description}
                onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: '#94a3b8' },
                }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: '#94a3b8' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submittingCoupon}
              sx={{ bgcolor: '#f59e0b', color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#d97706' } }}
            >
              {submittingCoupon ? <CircularProgress size={20} color="inherit" /> : 'Create Coupon'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <PublicFooter />
    </Box>
  );
}
